import { useMemo } from "react";
import { ExplainerPanel } from "./MaintenanceUi";
import SensorHistoryChart from "./SensorHistoryChart";
import { SensitivityPicker, useSensitivityDraft } from "./SensitivityPicker";
import {
  QUALITY_STYLE,
  QUALITY_TEXT,
  expectedText,
  stateText,
  valueQuality,
} from "./hangerSensorHistory";
import {
  DEFAULT_HANGER_SENSITIVITY,
  DEV_LIMIT,
  FLAT_LIMIT,
  SCALE_MAX,
  hangerDecision,
  hangerLimitTable,
  hangerSensitivityOf,
  isOkcDiffMode,
  okcDiffTable,
} from "./hangerSensitivity";
import { chartColors } from "../../../../Config/Colors";

// Bakım → "ASKI SENSÖRÜ" kutusunun altındaki açıklama paneli (#132). Fincan/değirmen
// panelinin (SensorExplainer) askı karşılığı: kartın sapan/düz kararı,
// hassasiyet → sapma sınırı tablosu, son okumalar grafiği ve kısa anlatım.
// Kart teşhis değerleri göz numarası taşımaz; aynı anda tek göz izlenir.

const LEVEL_MAX = 4095;

// Eksen kırık ölçekli: 0-255'te karar sınırları uçlara yakın (7 ve 243),
// doğrusal çizilse "yerinde" bölgesi görünmez. Sınır çevresi büyütülür;
// [değer, yüzde] durakları arasında doğrusal.
const DEV_STOPS = [
  [0, 0],
  [DEV_LIMIT, 30],
  [30, 60],
  [SCALE_MAX, 100],
];
const FLAT_STOPS = [
  [0, 0],
  [225, 40],
  [FLAT_LIMIT, 70],
  [SCALE_MAX, 100],
];

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

// Eski kart seti fark modu (WS#398): 0-255, eşik çevresi büyütülür.
const diffStops = (limit) => [
  [0, 0],
  [limit, 40],
  [Math.max(limit * 4, limit + 10), 80],
  [SCALE_MAX, 100],
];

/** Kırık ölçek: değer → eksen yüzdesi. */
export const stopPct = (stops, v) => {
  const x = Math.min(SCALE_MAX, Math.max(0, v));
  for (let i = 1; i < stops.length; i += 1) {
    const [v0, p0] = stops[i - 1];
    const [v1, p1] = stops[i];
    if (x <= v1) return p0 + ((x - v0) / (v1 - v0)) * (p1 - p0);
  }
  return 100;
};

/**
 * Tek değer ekseni (0-255): "yerinde" bölgesi yeşil, kalan "yerinde değil".
 * @param {"below"|"above"} inSide yerinde için değer sınırın altında mı üstünde mi
 */
function DecisionBar({ testId, label, value, limit, stops, inSide }) {
  const limitPct = stopPct(stops, limit);
  const hasLive = isNum(value);
  const valuePct = hasLive ? stopPct(stops, value) : null;
  const ok = hasLive && (inSide === "below" ? value < limit : value > limit);
  const inZone =
    inSide === "below"
      ? { left: 0, width: `${limitPct}%` }
      : { left: `${limitPct}%`, right: 0 };
  const outZone =
    inSide === "below"
      ? { left: `${limitPct}%`, right: 0 }
      : { left: 0, width: `${limitPct}%` };
  const rule = inSide === "below" ? `< ${limit}` : `> ${limit}`;

  return (
    <div data-testid={testId} className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-SemiBold text-gray-700">
          {label}{" "}
          <span className="font-Medium text-gray-400">
            (yerinde için {rule})
          </span>
        </span>
        {hasLive ? (
          <span
            data-testid={`${testId}-value`}
            className={`rounded px-1.5 font-SemiBold tabular-nums ${
              ok
                ? "bg-emerald-100 text-emerald-800"
                : "bg-rose-100 text-rose-800"
            }`}
          >
            Şu an {value}
          </span>
        ) : (
          <span className="text-gray-400">Canlı değer yok</span>
        )}
      </div>
      <div className="relative h-7 overflow-hidden rounded-md border border-gray-200">
        <div
          data-testid={`${testId}-in-zone`}
          className="absolute inset-y-0 bg-emerald-100"
          style={inZone}
        />
        <div className="absolute inset-y-0 bg-rose-50" style={outZone} />
        <div
          data-testid={`${testId}-limit`}
          className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-amber-500"
          style={{ left: `${limitPct}%` }}
        />
        {hasLive && (
          <div
            data-testid={`${testId}-marker`}
            className="absolute bottom-0 top-1.5 w-1.5 -translate-x-1/2 rounded-sm bg-main"
            style={{ left: `${valuePct}%` }}
          />
        )}
      </div>
      <div className="relative h-4 text-[10px] tabular-nums text-gray-500">
        {stops.map(([v, p]) => (
          <span
            key={v}
            className={`absolute whitespace-nowrap ${
              p === 0 ? "left-0" : p === 100 ? "right-0" : "-translate-x-1/2"
            } ${v === limit ? "font-SemiBold text-amber-700" : ""}`}
            style={p === 0 || p === 100 ? undefined : { left: `${p}%` }}
          >
            {v}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Ortalama seviye (0-4095) için ince şerit — yalnız konum, yön iddiası yok. */
function LevelStrip({ value }) {
  const hasLive = isNum(value);
  const pct = hasLive
    ? (Math.min(LEVEL_MAX, Math.max(0, value)) / LEVEL_MAX) * 100
    : null;
  return (
    <div data-testid="hanger-level" className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-SemiBold text-gray-700">Ortalama seviye</span>
        <span className="tabular-nums text-gray-500">
          {hasLive ? value : "—"}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-gray-100">
        {hasLive && (
          <div
            className="absolute -top-0.5 h-3 w-1.5 -translate-x-1/2 rounded-sm bg-main"
            style={{ left: `${pct}%` }}
          />
        )}
      </div>
      <div className="flex justify-between text-[10px] tabular-nums text-gray-400">
        <span>0</span>
        <span>{LEVEL_MAX}</span>
      </div>
    </div>
  );
}

const HISTORY_SERIES = [
  { id: "dev", key: "dev", label: "Sapan", color: chartColors.hangerDev },
  { id: "flat", key: "flat", label: "Düz", color: chartColors.hangerFlat },
  {
    id: "devLimit",
    key: "devLimit",
    label: `Sınır ${DEV_LIMIT}`,
    color: chartColors.hangerDevLimit,
    dashed: true,
  },
  {
    id: "flatLimit",
    key: "flatLimit",
    label: `Sınır ${FLAT_LIMIT}`,
    color: chartColors.hangerFlatLimit,
    dashed: true,
  },
];
const Y_RANGE = { min: 0, max: SCALE_MAX };
const DIFF_SERIES = [
  { id: "diff", key: "diff", label: "Fark", color: chartColors.hangerDev },
  { id: "diffLimit", key: "diffLimit", label: "Eşik", color: chartColors.hangerDevLimit, dashed: true },
];

const B = ({ children }) => (
  <span className="font-SemiBold text-gray-800">{children}</span>
);

/**
 * @param {object} props
 * @param {object|null} props.live son canlı askı yükü (yoksa null)
 * @param {{ receivedAt: number, data: object }[]} props.history son okumalar
 * @param {Function} [props.onSaveSensitivity] (value) => Promise<boolean>;
 *   yoksa hassasiyet kutuları salt-okunur
 */
export default function HangerSensorExplainer({
  live,
  history = [],
  onSaveSensitivity,
}) {
  const dev = live?.dev;
  const flat = live?.flat;
  // Eski kart seti askısı (v0.4.4 / v0.4.6+) tek değer gönderir: fark.
  const diffMode = isOkcDiffMode(live);
  const diffLimit = isNum(live?.diffThreshold) ? live.diffThreshold : 13;
  const saved = hangerSensitivityOf(live);
  const picker = useSensitivityDraft(saved, onSaveSensitivity);
  const decision =
    live?.state === 0
      ? "in"
      : live?.state === 1
        ? "out"
        : hangerDecision(dev, flat);
  const quality = live ? valueQuality(live) : null;
  const cup = live?.cup ?? history[history.length - 1]?.data?.cup ?? null;

  // Grafik yalnız izlenen gözün okumalarını gösterir (göz değişince karışmasın).
  const points = useMemo(
    () =>
      history
        .filter((e) => cup == null || e.data?.cup === cup)
        .map((e) => ({
          at: e.receivedAt,
          dev: isNum(e.data?.dev) ? e.data.dev : null,
          flat: isNum(e.data?.flat) ? e.data.flat : null,
          devLimit: DEV_LIMIT,
          flatLimit: FLAT_LIMIT,
          ...(diffMode
            ? {
                diff: isNum(e.data?.diff) ? e.data.diff : null,
                diffLimit: isNum(e.data?.diffThreshold) ? e.data.diffThreshold : 13,
              }
            : {}),
        })),
    [history, cup, diffMode],
  );
  const chartable = diffMode
    ? points.filter((p) => p.diff !== null).length >= 2
    : points.filter((p) => p.dev !== null && p.flat !== null).length >= 2;

  return (
    <ExplainerPanel
      testId="hanger-explainer"
      right={
        cup != null ? (
          <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-SemiBold text-gray-600">
            Göz {cup}
          </span>
        ) : null
      }
    >
      {/* Karar görseli */}
      <section className="flex flex-col gap-2">
        {diffMode ? (
          <DecisionBar
            testId="hanger-diff"
            label="Fark"
            value={live?.diff}
            limit={diffLimit}
            stops={diffStops(diffLimit)}
            inSide="below"
          />
        ) : (
          <>
        <DecisionBar
          testId="hanger-dev"
          label="Sapan örnek"
          value={dev}
          limit={DEV_LIMIT}
          stops={DEV_STOPS}
          inSide="below"
        />
        <DecisionBar
          testId="hanger-flat"
          label="Düz örnek"
          value={flat}
          limit={FLAT_LIMIT}
          stops={FLAT_STOPS}
          inSide="above"
        />
          </>
        )}
        <p className="text-[10px] text-gray-400">
          Yeşil: kol yerinde bölgesi. Ölçek sınır çevresinde büyütüldü.
        </p>
        {/* Eski kart setinde (OKC) ham seviye yok — yük null gönderir (WS#398). */}
        {live?.level !== null && <LevelStrip value={live?.level} />}

        <div
          data-testid="hanger-decision"
          className={`flex flex-col gap-0.5 rounded-lg border px-3 py-2 ${
            quality
              ? QUALITY_STYLE[quality]
              : "border-gray-200 bg-gray-50 text-gray-500"
          }`}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm font-SemiBold">
              Kartın kararı:{" "}
              {decision === "in"
                ? "Yerinde"
                : decision === "out"
                  ? "Yerinde değil"
                  : stateText(null)}
            </span>
            {quality && (
              <span
                data-testid="hanger-quality"
                className="text-xs font-SemiBold"
              >
                {QUALITY_TEXT[quality]}
              </span>
            )}
          </div>
          {live && (
            <span className="text-xs opacity-80">
              {expectedText(live.brewing)}
            </span>
          )}
        </div>
      </section>

      <SensitivityPicker
        title={diffMode ? "Hassasiyet → fark eşiği" : "Hassasiyet → sapma sınırı"}
        unit={diffMode ? "fark eşiği" : "sapma sınırı"}
        table={diffMode ? okcDiffTable() : hangerLimitTable()}
        saved={saved}
        fallback={DEFAULT_HANGER_SENSITIVITY}
        picker={picker}
        canWrite={typeof onSaveSensitivity === "function"}
      />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-SemiBold text-gray-600">
          Son okumalar ({points.length})
        </h4>
        {chartable ? (
          <SensorHistoryChart
            points={points}
            series={diffMode ? DIFF_SERIES : HISTORY_SERIES}
            yRange={Y_RANGE}
          />
        ) : (
          <p className="text-xs text-gray-400">
            Grafik için askı izlemeyi başlatın; en az iki okuma gerekir.
          </p>
        )}
      </section>

      <ul className="flex flex-col gap-1.5 text-xs text-gray-600 list-disc pl-4">
        <li>
          <B>Sensör neyi görüyor:</B> askı kolunun yerini tek bir yansımalı
          kızılötesi sensör görür. Kart her ölçüm penceresinde ~50 örnek alır.
        </li>
        <li>
          <B>Ortalama seviye:</B> pencerenin ortalaması (ham, 0-4095). Tek
          başına karar vermez; sabit mi, oynuyor mu, uca (0 ya da 4095) yapışık
          mı diye okunur.
        </li>
        <li>
          <B>Sapan / düz örnek:</B> her örneğin ortalamadan farkı sapma sınırını
          aşıyorsa "sapan", aşmıyorsa "düz" sayılır. ~2550 örnek sonunda iki
          sayı 0-255'e çevrilir; toplamları yaklaşık 255'tir.
        </li>
        <li>
          <B>Karar:</B> sapan {DEV_LIMIT}'nin altında VE düz {FLAT_LIMIT}'ün
          üstündeyse kol yerinde. Aksi halde yerinde değil. Yani kol yerindeyken
          sinyal sakin ve düz olmalı; dalgalı sinyal "yerinde değil" demektir.
        </li>
        <li>
          <B>Hassasiyeti artırınca</B> sapma sınırı küçülür; küçük dalgalanmalar
          da sapan sayılır ve kart daha kolay "yerinde değil" der.{" "}
          <B>Azaltınca</B> sınır büyür; yalnız büyük dalgalanma sayılır.
        </li>
        <li>
          <B>Kol yerindeyken "yerinde değil" diyorsa</B> (ortam ışığı titreşimi,
          parlama) hassasiyeti AZALTIN.
        </li>
        <li>
          <B>Kol yerinde değilken "yerinde" diyorsa / motor durmuyorsa</B> (koyu
          ya da kirli plastik, sensöre uzak kol) hassasiyeti ARTIRIN.
        </li>
        <li>
          <B>{DEFAULT_HANGER_SENSITIVITY} = fabrika</B> ayarı (sapma sınırı
          250).
        </li>
        <li>
          <B>Öneri:</B> değişiklikten sonra birkaç saniye bekleyin; kol
          yerindeyken ve değilken sapan/düz değerinin sınırdan uzak durduğunu
          kontrol edin.
        </li>
      </ul>
    </ExplainerPanel>
  );
}
